import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import InsertPhotoIcon from '@mui/icons-material/InsertPhoto';
import LocalGroceryStoreIcon from '@mui/icons-material/LocalGroceryStore';
import PersonIcon from '@mui/icons-material/Person';
import ScheduleIcon from '@mui/icons-material/Schedule';
import StoreIcon from '@mui/icons-material/Store';
import { Alert, Avatar, Box, Button, Divider, Grid, IconButton, Paper, Tooltip, Typography } from '@mui/material';
import { blue } from '@mui/material/colors';
import { Display, FlightInfo, getDestinationContactFromOrderInfo, getRequesterContactFromOrderInfo, getSizeLabel, getTotalVolumeLabel, getTotalWeightLabel, getVolumeLabel, getWeightLabel, isBusinessDestinationInfo, isBusinessRequesterInfo, isMaritimeOrderInfo, OrderInfo } from "adoms-common-lib";
import React, { useEffect } from 'react';
import ReceiveStatusInfo from '../atoms/ReceiveStatusInfo';
import { OrderImageDialog } from '../molecule/OrderImageDialog';
import { OrderInfoDisconnectDialog } from '../molecule/OrderInfoDisconnectDialog';
import { OrderInfoEditDiglog } from '../molecule/OrderInfoEditDiglog';
import { ReceiveStatusChangeDialog } from '../molecule/ReceiveStatusChangeDialog';
import FlightOrderCombinationDialogForOperation from './FlightOrderCombinationDialogForOperation';

type OrderInfoGridProps = {
    selectedFlightInfo: FlightInfo,
    setSavedOrderInfo: React.Dispatch<React.SetStateAction<boolean>>
    orderInfoList: OrderInfo[]
};

export const OrderInfoGrid: React.FC<OrderInfoGridProps> = (props) => {

    const [displayOrderInfoList, setDisplayOrderInfoList] = React.useState<OrderInfo[]>(props.orderInfoList);
    const [openDialogs, setOpenDialogs] = React.useState<{
        orderImage: string | null;
        receiveStatus: string | null;
        orderInfoEdit: string | null;
        orderInfoDisconnect: string | null;
    }>({
        orderImage: null,
        receiveStatus: null,
        orderInfoEdit: null,
        orderInfoDisconnect: null,
    });
    const [isFlightOrderCombinationDialogOpen, setFlightOrderCombinationDialogOpen] = React.useState(false);

    useEffect(() => {
        setDisplayOrderInfoList(props.orderInfoList);
    }, [props.orderInfoList]);

    const subtitleValueRows = (subtitleValueList: { subtitle: string, value: string }[]) => (
        <Grid item container spacing={1} direction={"column"}>
            {subtitleValueList.map((subtitleValue, index) => (
                <Grid item container spacing={1} direction={"row"} flexWrap="nowrap" key={index}>
                    <Grid item>
                        <Typography width="120px"
                            color="textSecondary"
                            whiteSpace="pre-wrap">{subtitleValue.subtitle}</Typography>
                    </Grid>
                    <Grid item>
                        <Typography whiteSpace="pre-wrap" sx={{ fontWeight: 'bold' }}>
                            {subtitleValue.value}
                        </Typography>
                    </Grid>
                </Grid>
            ))}
        </Grid>
    );

    const orderInfoTypographyGrid = (icon: JSX.Element, title: string, subtitleValueList: { subtitle: string, value: string }[]) => {
        return (
            <Grid item container spacing={2} direction={"row"} flexWrap="nowrap">
                <Grid item paddingTop={2}>
                    {icon}
                </Grid>
                <Grid item container spacing={1} direction={"column"}>
                    <Grid item >
                        <Typography color="textSecondary" variant="subtitle1" sx={{ fontWeight: 'bold' }}>{title}</Typography>
                    </Grid>
                    {subtitleValueRows(subtitleValueList)}
                </Grid>
            </Grid>
        )
    };

    const handleJmsdfUnitSubtitleValue = (unit: { unitName: string, lastname: string, firstname: string, tel: string, mailaddress: string }): { subtitle: string, value: string }[] => {
        return [
            { subtitle: "部隊名", value: unit.unitName && unit.unitName !== "" ? unit.unitName : "設定なし" },
            { subtitle: "氏名", value: `${unit.lastname} ${unit.firstname}`.trim() !== "" ? `${unit.lastname} ${unit.firstname}` : "設定なし" },
            { subtitle: "電話番号", value: unit.tel && unit.tel !== "" ? unit.tel : "設定なし" },
            { subtitle: "メールアドレス", value: unit.mailaddress && unit.mailaddress !== "" ? unit.mailaddress : "設定なし" },
        ];
    };

    const maritimeRequesterInfoTypographyGrid = (displayOrderInfo: OrderInfo) => {
        if (!isMaritimeOrderInfo(displayOrderInfo)) return null;

        return (
            <Grid item container spacing={2} direction={"row"} flexWrap="nowrap">
                <Grid item paddingTop={2}>
                    <PersonIcon sx={{ color: "#797979", marginTop: "3px" }} />
                </Grid>
                <Grid item container spacing={1} direction={"column"}>
                    <Grid item>
                        <Typography color="textSecondary" variant="subtitle1" sx={{ fontWeight: 'bold' }}>荷送人情報</Typography>
                    </Grid>

                    <Grid item>
                        <Typography color="textSecondary" sx={{ fontWeight: 'bold' }}>補給本部</Typography>
                    </Grid>
                    {subtitleValueRows(handleJmsdfUnitSubtitleValue(displayOrderInfo.requester.supplyHeadquartersInfo))}

                    <Divider sx={{ my: 1 }} />

                    <Grid item>
                        <Typography color="textSecondary" sx={{ fontWeight: 'bold' }}>補給元部隊</Typography>
                    </Grid>
                    {subtitleValueRows(handleJmsdfUnitSubtitleValue(displayOrderInfo.requester.supplySourceUnitInfo))}
                </Grid>
            </Grid>
        );
    };

    const handleRequesterSubtitleValue = (displayOrderInfo: OrderInfo): { subtitle: string, value: string }[] => {
        if (isMaritimeOrderInfo(displayOrderInfo)) {
            // Maritime の「荷送人情報」は補給本部/補給元部隊を表示するため、ここでは使わない
            return [];
        }

        const companyNameSubtitleValue = isBusinessRequesterInfo(
            displayOrderInfo.requester) ? [{ subtitle: "会社名", value: displayOrderInfo.requester.companyName }] : [];

        const requesterContact = getRequesterContactFromOrderInfo(displayOrderInfo);

        const subtitleValueList = [
            {
                subtitle: "氏名",
                value: requesterContact.lastname + " " + requesterContact.firstname
            },
            {
                subtitle: "電話番号", value: (requesterContact.tel && requesterContact.tel !== "")
                    ? requesterContact.tel : "設定なし"
            },
            {
                subtitle: "メールアドレス", value: (requesterContact.mailaddress && requesterContact.mailaddress !== "")
                    ? requesterContact.mailaddress : "設定なし"
            }
        ]

        if (companyNameSubtitleValue != null) {
            return companyNameSubtitleValue.concat(subtitleValueList);
        }
        return subtitleValueList;
    }

    const handleDestinationSubtitleValue = (displayOrderInfo: OrderInfo): { subtitle: string, value: string }[] => {
        if (isMaritimeOrderInfo(displayOrderInfo)) {
            return handleJmsdfUnitSubtitleValue(displayOrderInfo.destination.transportRequestUnit);
        }

        const companyNameSubtitleValue = isBusinessDestinationInfo(
            displayOrderInfo.destination) ? [{ subtitle: "会社名", value: displayOrderInfo.destination.companyName }] : [];
        const destinationContact = getDestinationContactFromOrderInfo(displayOrderInfo);
        const subtitleValueList = [
            {
                subtitle: "氏名",
                value: destinationContact.lastname + " " + destinationContact.firstname
            },
            {
                subtitle: "電話番号", value: (destinationContact.tel && destinationContact.tel !== "")
                    ? destinationContact.tel : "設定なし"
            },
            {
                subtitle: "メールアドレス", value: (destinationContact.mailaddress && destinationContact.mailaddress !== "")
                    ? destinationContact.mailaddress : "設定なし"
            }
        ]

        if (companyNameSubtitleValue != null) {
            return companyNameSubtitleValue.concat(subtitleValueList);
        }
        return subtitleValueList;
    }

    return (displayOrderInfoList && displayOrderInfoList.length > 0 ?
        <React.Fragment>
            <Box height={`calc(100vh - 240px)`} overflow={"auto"} width="100%">
                {displayOrderInfoList.map((displayOrderInfo, index) => (
                    <Paper
                        key={displayOrderInfo.orderID}
                        sx={{ padding: 2, marginBottom: index < displayOrderInfoList.length - 1 ? 2 : 0 }}
                        variant="outlined"
                    >
                        <Grid container
                            spacing={2}
                            direction={"column"}
                            xs={12}
                        >
                            <Grid item>
                                <Grid container item xs={12} justifyContent="space-between">
                                    <Grid item>
                                        <Button sx={{ textTransform: 'none', padding: 0 }} onClick={() => setOpenDialogs(prev => ({ ...prev, receiveStatus: displayOrderInfo.orderID }))}>
                                            <ReceiveStatusInfo status={displayOrderInfo?.receiveStatus} isOkinawaBlood={displayOrderInfo.businessPartnerId === "OKINAWA_BLOOD"} />
                                        </Button>
                                    </Grid>
                                    <Grid item>
                                        <Grid container spacing={1} direction="row">
                                            <Grid item>
                                                <Tooltip title="配送情報変更">
                                                    <IconButton
                                                        sx={{ padding: 0 }}
                                                        onClick={() => setOpenDialogs(prev => ({ ...prev, orderInfoEdit: displayOrderInfo.orderID }))}>
                                                        <Avatar variant="rounded" sx={{ bgcolor: blue[500] }}>
                                                            <EditIcon />
                                                        </Avatar>
                                                    </IconButton>
                                                </Tooltip>
                                            </Grid>
                                            <Grid item>
                                                <Tooltip title="注文内容">
                                                    <div>
                                                        <IconButton
                                                            sx={{ padding: 0 }}
                                                            onClick={() => setOpenDialogs(prev => ({ ...prev, orderImage: displayOrderInfo.orderID }))
                                                            }
                                                            disabled={displayOrderInfo.receiptFileName === undefined}>
                                                            <Avatar variant="rounded"
                                                                sx={{
                                                                    bgcolor: displayOrderInfo.receiptFileName !== undefined ?
                                                                        blue[500] : undefined
                                                                }}>
                                                                <InsertPhotoIcon />
                                                            </Avatar>
                                                        </IconButton>
                                                    </div>
                                                </Tooltip>
                                            </Grid>
                                            <Grid item>
                                                <Tooltip title="配送情報の紐づけ削除">
                                                    <div>
                                                        <IconButton
                                                            sx={{ padding: 0 }}
                                                            onClick={() => setOpenDialogs(prev => ({ ...prev, orderInfoDisconnect: displayOrderInfo.orderID }))
                                                            }
                                                        >
                                                            <Avatar variant="rounded" sx={{ bgcolor: blue[500] }}>
                                                                {/* ゴミ箱アイコンを表示 */}
                                                                <DeleteIcon />
                                                            </Avatar>
                                                        </IconButton>
                                                    </div>
                                                </Tooltip>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Grid>
                                <Grid item>
                                    <Typography variant='subtitle2' color="textSecondary" sx={{ marginTop: 1 }}>
                                        {displayOrderInfo.businessPartnerId + " " + displayOrderInfo.orderID}
                                    </Typography>
                                </Grid>
                            </Grid>
                            <Grid item container spacing={2}>
                                {isMaritimeOrderInfo(displayOrderInfo) ? (
                                    orderInfoTypographyGrid(
                                        <InfoOutlinedIcon sx={{ color: "#797979", marginTop: "3px" }} />,
                                        "配送情報",
                                        [
                                            {
                                                subtitle: "配送ルート",
                                                value: displayOrderInfo.transportRoute ? displayOrderInfo.transportRoute : "なし"
                                            },
                                            {
                                                subtitle: "補給本部伝票番号",
                                                value: displayOrderInfo.supplyHqVoucherNumber && displayOrderInfo.supplyHqVoucherNumber !== "" ? displayOrderInfo.supplyHqVoucherNumber : "なし"
                                            },
                                            {
                                                subtitle: "日通運送番号",
                                                value: displayOrderInfo.carrierWaybillNumber && displayOrderInfo.carrierWaybillNumber !== "" ? displayOrderInfo.carrierWaybillNumber : "なし"
                                            }
                                        ]
                                    )
                                ) : null}
                                {orderInfoTypographyGrid(
                                    <ScheduleIcon sx={{ color: "#797979", marginTop: "3px" }} />,
                                    "配送希望日時",
                                    [{ subtitle: "日付", value: displayOrderInfo?.preferredDeliveryDate },
                                    { subtitle: "時間帯", value: displayOrderInfo.preferredDeliveryTime }]
                                )}
                                {isMaritimeOrderInfo(displayOrderInfo)
                                    ? maritimeRequesterInfoTypographyGrid(displayOrderInfo)
                                    : orderInfoTypographyGrid(
                                        <PersonIcon sx={{ color: "#797979", marginTop: "3px" }} />,
                                        "荷送人情報",
                                        handleRequesterSubtitleValue(displayOrderInfo),
                                    )}
                                {orderInfoTypographyGrid(
                                    <StoreIcon sx={{ color: "#797979", marginTop: "3px" }} />,
                                    "荷受人情報",
                                    handleDestinationSubtitleValue(displayOrderInfo),
                                )}
                                <Grid item container spacing={2} direction={"row"} flexWrap="nowrap">
                                    <Grid item paddingTop={2}>
                                        <LocalGroceryStoreIcon sx={{ color: "#797979", marginTop: "3px" }} />
                                    </Grid>
                                    <Grid item container spacing={1} direction={"column"}>
                                        <Grid item>
                                            <Typography color="textSecondary" variant="subtitle1" sx={{ fontWeight: 'bold' }}>商品情報</Typography>
                                        </Grid>
                                        <Grid item container spacing={1} direction={"column"}>
                                            <Grid item container spacing={1} direction={"row"} flexWrap="nowrap">
                                                <Grid item>
                                                    <Typography width="120px" color="textSecondary" whiteSpace="pre-wrap">合計重量</Typography>
                                                </Grid>
                                                <Grid item>
                                                    <Typography whiteSpace="pre-wrap" sx={{ fontWeight: 'bold' }}>
                                                        {isMaritimeOrderInfo(displayOrderInfo)
                                                            ? getTotalWeightLabel(displayOrderInfo.goods)
                                                            : `${String(displayOrderInfo.goods[0].weight)}g`}
                                                    </Typography>
                                                </Grid>
                                            </Grid>

                                            {isMaritimeOrderInfo(displayOrderInfo) ? (
                                                <Grid item container spacing={1} direction={"row"} flexWrap="nowrap">
                                                    <Grid item>
                                                        <Typography width="120px" color="textSecondary" whiteSpace="pre-wrap">合計体積</Typography>
                                                    </Grid>
                                                    <Grid item>
                                                        <Typography whiteSpace="pre-wrap" sx={{ fontWeight: 'bold' }}>
                                                            {getTotalVolumeLabel(displayOrderInfo.goods)}
                                                        </Typography>
                                                    </Grid>
                                                </Grid>
                                            ) : null}

                                            {displayOrderInfo.goods.map((good, i) => (
                                                <React.Fragment key={i}>
                                                    <Divider sx={{ my: 1 }} />
                                                    <Grid item>
                                                        <Typography color="textSecondary" sx={{ fontWeight: 'bold' }}>{`商品${i + 1}`}</Typography>
                                                    </Grid>
                                                    <Grid item container spacing={1} direction={"row"} flexWrap="nowrap">
                                                        <Grid item>
                                                            <Typography width="120px" color="textSecondary" whiteSpace="pre-wrap">商品名</Typography>
                                                        </Grid>
                                                        <Grid item>
                                                            <Typography whiteSpace="pre-wrap" sx={{ fontWeight: 'bold' }}>{good.name}</Typography>
                                                        </Grid>
                                                    </Grid>
                                                    {isMaritimeOrderInfo(displayOrderInfo) ? (
                                                        <React.Fragment>
                                                            <Grid item container spacing={1} direction={"row"} flexWrap="nowrap">
                                                                <Grid item>
                                                                    <Typography width="120px" color="textSecondary" whiteSpace="pre-wrap">寸法</Typography>
                                                                </Grid>
                                                                <Grid item>
                                                                    <Typography whiteSpace="pre-wrap" sx={{ fontWeight: 'bold' }}>{getSizeLabel(good)}</Typography>
                                                                </Grid>
                                                            </Grid>
                                                            <Grid item container spacing={1} direction={"row"} flexWrap="nowrap">
                                                                <Grid item>
                                                                    <Typography width="120px" color="textSecondary" whiteSpace="pre-wrap">体積</Typography>
                                                                </Grid>
                                                                <Grid item>
                                                                    <Typography whiteSpace="pre-wrap" sx={{ fontWeight: 'bold' }}>{getVolumeLabel(good)}</Typography>
                                                                </Grid>
                                                            </Grid>
                                                            <Grid item container spacing={1} direction={"row"} flexWrap="nowrap">
                                                                <Grid item>
                                                                    <Typography width="120px" color="textSecondary" whiteSpace="pre-wrap">重さ</Typography>
                                                                </Grid>
                                                                <Grid item>
                                                                    <Typography whiteSpace="pre-wrap" sx={{ fontWeight: 'bold' }}>{getWeightLabel(good)}</Typography>
                                                                </Grid>
                                                            </Grid>
                                                        </React.Fragment>
                                                    ) : (
                                                        <React.Fragment>
                                                            <Grid item container spacing={1} direction={"row"} flexWrap="nowrap">
                                                                <Grid item>
                                                                    <Typography width="120px" color="textSecondary" whiteSpace="pre-wrap">ボックスの種類</Typography>
                                                                </Grid>
                                                                <Grid item>
                                                                    <Typography whiteSpace="pre-wrap" sx={{ fontWeight: 'bold' }}>{good.packageType ?? "設定なし"}</Typography>
                                                                </Grid>
                                                            </Grid>
                                                            <Grid item container spacing={1} direction={"row"} flexWrap="nowrap">
                                                                <Grid item>
                                                                    <Typography width="120px" color="textSecondary" whiteSpace="pre-wrap">温度</Typography>
                                                                </Grid>
                                                                <Grid item>
                                                                    <Typography whiteSpace="pre-wrap" sx={{ fontWeight: 'bold' }}>
                                                                        {(good.handlingInstructions != null && good.handlingInstructions.length > 0) ? good.handlingInstructions[0] : "室温"}
                                                                    </Typography>
                                                                </Grid>
                                                            </Grid>
                                                        </React.Fragment>
                                                    )}
                                                </React.Fragment>
                                            ))}
                                        </Grid>
                                    </Grid>
                                </Grid>
                                {orderInfoTypographyGrid(
                                    <InfoOutlinedIcon sx={{ color: "#797979", marginTop: "3px" }} />,
                                    "その他",
                                    [{ subtitle: "配送依頼日", value: Display.getDateStringFromISO8601(displayOrderInfo.orderDate) },
                                    {
                                        subtitle: "お客様共有備考", value: (displayOrderInfo.remarks && displayOrderInfo.remarks !== "")
                                            ? displayOrderInfo.remarks : "設定なし"
                                    }
                                    ]
                                )}
                            </Grid>
                        </Grid>
                        <ReceiveStatusChangeDialog
                            displayOrderInfo={displayOrderInfo}
                            isReceiveStatusDialogOpen={openDialogs.receiveStatus === displayOrderInfo.orderID}
                            setReceiveStatusDialogOpen={() => setOpenDialogs(prev => ({ ...prev, receiveStatus: null }))
                            }
                            setSavedOrderInfo={props.setSavedOrderInfo}
                        />
                        <OrderImageDialog
                            displayOrderInfo={displayOrderInfo}
                            isOrderImageDialogOpen={openDialogs.orderImage === displayOrderInfo.orderID}
                            setOrderImageDialogOpen={() => setOpenDialogs(prev => ({ ...prev, orderImage: null }))
                            }
                        />
                        <OrderInfoEditDiglog
                            displayOrderInfo={displayOrderInfo}
                            isOrderInfoEditDialogOpen={openDialogs.orderInfoEdit === displayOrderInfo.orderID}
                            setOrderInfoEditDialogOpen={() => setOpenDialogs(prev => ({ ...prev, orderInfoEdit: null }))
                            }
                            setSavedOrderInfo={props.setSavedOrderInfo}
                        />
                        <OrderInfoDisconnectDialog
                            flight={props.selectedFlightInfo}
                            isOpen={openDialogs.orderInfoDisconnect === displayOrderInfo.orderID}
                            continueConnectOrderIDs={
                                props.orderInfoList.filter(orderInfo => orderInfo.orderID != displayOrderInfo.orderID).map(orderInfo => orderInfo.orderID)
                            }
                            onClose={() => setOpenDialogs(prev => ({ ...prev, orderInfoDisconnect: null }))
                            }
                        />
                    </Paper>
                ))}
                <Button
                    variant="contained"
                    color="primary"
                    onClick={() => setFlightOrderCombinationDialogOpen(true)}
                    disabled={props.orderInfoList.length >= 2}
                    sx={{ float: "right", padding: 1, marginTop: 2 }}
                >
                    配送情報紐付
                </Button>
            </Box>
            <FlightOrderCombinationDialogForOperation
                flight={props.selectedFlightInfo}
                isOpen={isFlightOrderCombinationDialogOpen}
                onClose={() => setFlightOrderCombinationDialogOpen(false)}
                addedOrder={props.orderInfoList}
            />
        </React.Fragment>
        :
        <React.Fragment>
            <Grid container>
                <Grid item xs={12}>
                    <Alert severity="info" >配送情報がありません</Alert>
                </Grid>
                <Grid item xs={12}>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => setFlightOrderCombinationDialogOpen(true)}
                        sx={{ float: "right", padding: 1, marginTop: 2 }}
                    >
                        配送情報紐付
                    </Button>
                </Grid>
            </Grid>
            <FlightOrderCombinationDialogForOperation
                flight={props.selectedFlightInfo}
                isOpen={isFlightOrderCombinationDialogOpen}
                onClose={() => setFlightOrderCombinationDialogOpen(false)}
                addedOrder={props.orderInfoList}
            />
        </React.Fragment>
    )
};